import { createSlice } from '@reduxjs/toolkit';

// ---------------------------------------------------------------------------
// Persist layout preferences to localStorage
// ---------------------------------------------------------------------------

const LAYOUT_STORAGE_KEY = 'mail_layout_prefs';

const loadLayoutPrefs = () => {
  try {
    const raw = localStorage.getItem(LAYOUT_STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

const saveLayoutPrefs = (layout) => {
  try {
    localStorage.setItem(LAYOUT_STORAGE_KEY, JSON.stringify(layout));
  } catch {
    // Ignore storage errors
  }
};

const persisted = loadLayoutPrefs();

// ---------------------------------------------------------------------------
// Initial State
// ---------------------------------------------------------------------------

const initialState = {
  // -----------------------------------------------------------------------
  // Core mail state
  // -----------------------------------------------------------------------

  // Currently active folder ID
  selectedFolderId: null,

  // Currently selected thread (conversationId)
  selectedThreadId: null,

  // IDs of expanded messages within the selected thread
  expandedMessageIds: [],

  // App mode
  uiMode: 'mail', // 'mail' | 'compose' | 'search' | 'settings'

  // -----------------------------------------------------------------------
  // Ribbon
  // -----------------------------------------------------------------------
  ribbon: {
    mode: 'mail',        // 'mail' | 'compose'
    activeTabId: 'home', // matches MailRibbon TABS ids: 'home' | 'view' | 'help'
  },

  // -----------------------------------------------------------------------
  // Layout (persisted)
  // -----------------------------------------------------------------------
  layout: {
    sidebarWidth: persisted.sidebarWidth || 240,
    listPaneWidth: persisted.listPaneWidth || 380,
    readingPanePosition: persisted.readingPanePosition || 'right', // 'right' | 'bottom' | 'off'
    isCompact: persisted.isCompact || false,
  },

  // -----------------------------------------------------------------------
  // Reading pane
  // -----------------------------------------------------------------------
  readingPane: {
    // Scroll positions per thread: { [conversationId]: number }
    scrollPositions: {},
    zoom: 100,
    // Active inline compose session in reading pane
    inlineComposeSessionId: null,
  },

  // -----------------------------------------------------------------------
  // Notifications / toasts
  // -----------------------------------------------------------------------
  notifications: [],
  // Each: { id, type: 'success'|'info'|'warning'|'error', message, autoDismissMs }

  // Bell-panel notification history (persisted across folder switches)
  notificationHistory: [],
  // Each: { id, type: 'new_mail'|'reply', sender, subject, conversationId, folderId, receivedAt, isRead }
  unreadNotificationCount: 0,

  // -----------------------------------------------------------------------
  // Draft editing — message ID of draft currently being opened for editing
  // -----------------------------------------------------------------------
  draftEditMessageId: null,

  // -----------------------------------------------------------------------
  // Context menu
  // -----------------------------------------------------------------------
  contextMenu: null,
  // { x, y, targetId, targetType: 'thread'|'folder'|'message' }

  // -----------------------------------------------------------------------
  // Drag/drop
  // -----------------------------------------------------------------------
  dragDrop: null,
  // { draggingThreadId, overFolderId }

  // -----------------------------------------------------------------------
  // Search
  // -----------------------------------------------------------------------
  search: {
    query: '',
    committedQuery: '',
    scope: 'all',      // 'all' | 'mail' | 'files' | 'people'
    isActive: false,
    filters: {
      dateFrom: null,
      dateTo: null,
      from: null,
      to: null,
      hasAttachments: null,
      isUnread: null,
      categories: [],
      importance: null,
    },
    phase: 'idle', // 'idle' | 'focused' | 'suggesting' | 'searching' | 'results' | 'error'
    results: {
      threadIds: [],
      messageIds: [],
      total: 0,
      nextCursor: null,
    },
    suggestions: [],
    recentQueries: [],
    isFetchingMore: false,
    error: null,
  },

  // -----------------------------------------------------------------------
  // Connectivity
  // -----------------------------------------------------------------------
  graphStatus: 'connected', // 'connected' | 'degraded' | 'disconnected'
};

// ---------------------------------------------------------------------------
// Slice
// ---------------------------------------------------------------------------

const mailUiSlice = createSlice({
  name: 'mailUi',
  initialState,
  reducers: {
    // -----------------------------------------------------------------------
    // Folder + Thread selection
    // -----------------------------------------------------------------------
    setSelectedFolder(state, action) {
      const { folderId } = action.payload;
      if (state.selectedFolderId !== folderId) {
        state.selectedFolderId = folderId;
        state.selectedThreadId = null;
        state.expandedMessageIds = [];
        state.readingPane.inlineComposeSessionId = null;
      }
    },

    setSelectedThread(state, action) {
      const { conversationId, latestMessageId } = action.payload;
      state.selectedThreadId = conversationId;
      state.uiMode = 'mail';
      state.readingPane.inlineComposeSessionId = null;

      // Always expand latest message by default
      state.expandedMessageIds = latestMessageId ? [latestMessageId] : [];
    },

    clearSelectedThread(state) {
      state.selectedThreadId = null;
      state.expandedMessageIds = [];
      state.readingPane.inlineComposeSessionId = null;
    },

    // -----------------------------------------------------------------------
    // Message accordion expansion
    // -----------------------------------------------------------------------
    expandMessage(state, action) {
      const { messageId } = action.payload;
      if (!state.expandedMessageIds.includes(messageId)) {
        state.expandedMessageIds.push(messageId);
      }
    },

    collapseMessage(state, action) {
      const { messageId } = action.payload;
      state.expandedMessageIds = state.expandedMessageIds.filter(
        (id) => id !== messageId
      );
    },

    toggleMessage(state, action) {
      const { messageId } = action.payload;
      const idx = state.expandedMessageIds.indexOf(messageId);
      if (idx === -1) {
        state.expandedMessageIds.push(messageId);
      } else {
        state.expandedMessageIds.splice(idx, 1);
      }
    },

    expandAllMessages(state, action) {
      const { messageIds } = action.payload;
      state.expandedMessageIds = [...new Set([...state.expandedMessageIds, ...messageIds])];
    },

    collapseAllMessages(state) {
      state.expandedMessageIds = [];
    },

    // -----------------------------------------------------------------------
    // UI Mode
    // -----------------------------------------------------------------------
    setUiMode(state, action) {
      state.uiMode = action.payload; // 'mail' | 'compose' | 'search' | 'settings'
    },

    // -----------------------------------------------------------------------
    // Ribbon
    // -----------------------------------------------------------------------
    setRibbonMode(state, action) {
      const mode = action.payload; // 'mail' | 'compose'
      state.ribbon.mode = mode;
      state.ribbon.activeTabId = mode === 'compose' ? 'compose.message' : 'home';
    },

    setRibbonTab(state, action) {
      state.ribbon.activeTabId = action.payload;
    },

    // -----------------------------------------------------------------------
    // Layout
    // -----------------------------------------------------------------------
    setSidebarWidth(state, action) {
      state.layout.sidebarWidth = action.payload;
      saveLayoutPrefs(state.layout);
    },

    setListPaneWidth(state, action) {
      state.layout.listPaneWidth = action.payload;
      saveLayoutPrefs(state.layout);
    },

    setReadingPanePosition(state, action) {
      state.layout.readingPanePosition = action.payload;
      saveLayoutPrefs(state.layout);
    },

    setCompactMode(state, action) {
      state.layout.isCompact = action.payload;
      saveLayoutPrefs(state.layout);
    },

    // -----------------------------------------------------------------------
    // Reading pane
    // -----------------------------------------------------------------------
    saveScrollPosition(state, action) {
      const { conversationId, scrollTop } = action.payload;
      state.readingPane.scrollPositions[conversationId] = scrollTop;
    },

    setReadingPaneZoom(state, action) {
      state.readingPane.zoom = action.payload;
    },

    setInlineComposeSession(state, action) {
      state.readingPane.inlineComposeSessionId = action.payload;
      if (action.payload) {
        state.uiMode = 'compose';
        state.ribbon.mode = 'compose';
        state.ribbon.activeTabId = 'compose.message';
      }
    },

    clearInlineCompose(state) {
      state.readingPane.inlineComposeSessionId = null;
      state.uiMode = 'mail';
      state.ribbon.mode = 'mail';
      state.ribbon.activeTabId = 'home';
    },

    // -----------------------------------------------------------------------
    // Notifications
    // -----------------------------------------------------------------------
    pushNotification(state, action) {
      const notification = {
        id: `notif_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
        type: 'info',
        autoDismissMs: 4000,
        ...action.payload,
        createdAt: new Date().toISOString(),
      };
      state.notifications.push(notification);
      // Keep max 5 notifications
      if (state.notifications.length > 5) {
        state.notifications.shift();
      }
    },

    dismissNotification(state, action) {
      const { id } = action.payload;
      state.notifications = state.notifications.filter((n) => n.id !== id);
    },

    clearAllNotifications(state) {
      state.notifications = [];
    },

    // Add entry to bell-panel history (called by OutlookSocketListener on new mail)
    addMailNotification(state, action) {
      const entry = {
        id: `mn_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
        isRead: false,
        receivedAt: new Date().toISOString(),
        ...action.payload, // type, sender, subject, conversationId, folderId
      };
      state.notificationHistory.unshift(entry);
      if (state.notificationHistory.length > 50) {
        state.notificationHistory.length = 50;
      }
      state.unreadNotificationCount += 1;
    },

    // Mark all bell-panel entries as read (called when panel opens)
    markAllNotificationsRead(state) {
      state.unreadNotificationCount = 0;
      state.notificationHistory.forEach((n) => { n.isRead = true; });
    },

    // Remove one entry from history
    dismissMailNotification(state, action) {
      state.notificationHistory = state.notificationHistory.filter(
        (n) => n.id !== action.payload
      );
    },

    // -----------------------------------------------------------------------
    // Draft editing
    // -----------------------------------------------------------------------
    setDraftEdit(state, action) {
      state.draftEditMessageId = action.payload; // messageId string
    },

    clearDraftEdit(state) {
      state.draftEditMessageId = null;
    },

    // -----------------------------------------------------------------------
    // Context menu
    // -----------------------------------------------------------------------
    openContextMenu(state, action) {
      state.contextMenu = action.payload;
      // { x, y, targetId, targetType }
    },

    closeContextMenu(state) {
      state.contextMenu = null;
    },

    // -----------------------------------------------------------------------
    // Drag/Drop
    // -----------------------------------------------------------------------
    setDraggingThread(state, action) {
      const { threadId } = action.payload;
      state.dragDrop = { draggingThreadId: threadId, overFolderId: null };
    },

    setDragOverFolder(state, action) {
      const { folderId } = action.payload;
      if (state.dragDrop) {
        state.dragDrop.overFolderId = folderId;
      }
    },

    clearDragDrop(state) {
      state.dragDrop = null;
    },

    // -----------------------------------------------------------------------
    // Search
    // -----------------------------------------------------------------------
    setSearchQuery(state, action) {
      state.search.query = action.payload;
      if (!action.payload) {
        state.search.phase = 'idle';
        state.search.isActive = false;
        state.search.committedQuery = '';
        state.search.results = { threadIds: [], messageIds: [], total: 0, nextCursor: null };
      }
    },

    focusSearch(state) {
      state.search.phase = 'focused';
      state.uiMode = 'search';
    },

    commitSearchQuery(state, action) {
      state.search.committedQuery = action.payload || state.search.query;
      state.search.phase = 'searching';
      state.search.isActive = true;
    },

    setSearchScope(state, action) {
      state.search.scope = action.payload;
    },

    setSearchFilter(state, action) {
      const { key, value } = action.payload;
      state.search.filters[key] = value;
    },

    clearSearchFilter(state, action) {
      const { key } = action.payload;
      const defaults = initialState.search.filters;
      state.search.filters[key] = defaults[key];
    },

    clearAllSearchFilters(state) {
      state.search.filters = { ...initialState.search.filters };
    },

    setSearchResults(state, action) {
      const { threadIds, messageIds, total, nextCursor } = action.payload;
      state.search.results = { threadIds, messageIds, total, nextCursor };
      state.search.phase = 'results';
      state.search.error = null;
    },

    appendSearchResults(state, action) {
      const { threadIds, messageIds, nextCursor } = action.payload;
      state.search.results.threadIds.push(...threadIds);
      state.search.results.messageIds.push(...messageIds);
      state.search.results.nextCursor = nextCursor;
      state.search.isFetchingMore = false;
    },

    setSearchSuggestions(state, action) {
      state.search.suggestions = action.payload;
      if (state.search.query && state.search.phase === 'focused') {
        state.search.phase = 'suggesting';
      }
    },

    setSearchPhase(state, action) {
      state.search.phase = action.payload;
    },

    setSearchError(state, action) {
      state.search.phase = 'error';
      state.search.error = action.payload;
    },

    addRecentQuery(state, action) {
      const query = action.payload;
      state.search.recentQueries = [
        query,
        ...state.search.recentQueries.filter((q) => q !== query),
      ].slice(0, 10);
    },

    removeRecentQuery(state, action) {
      state.search.recentQueries = state.search.recentQueries.filter(
        (q) => q !== action.payload
      );
    },

    clearSearch(state) {
      state.search = { ...initialState.search };
      state.uiMode = 'mail';
    },

    // -----------------------------------------------------------------------
    // Graph connectivity
    // -----------------------------------------------------------------------
    setGraphStatus(state, action) {
      state.graphStatus = action.payload; // 'connected' | 'degraded' | 'disconnected'
    },
  },
});

export const {
  setSelectedFolder,
  setSelectedThread,
  clearSelectedThread,
  expandMessage,
  collapseMessage,
  toggleMessage,
  expandAllMessages,
  collapseAllMessages,
  setUiMode,
  setRibbonMode,
  setRibbonTab,
  setSidebarWidth,
  setListPaneWidth,
  setReadingPanePosition,
  setCompactMode,
  saveScrollPosition,
  setReadingPaneZoom,
  setInlineComposeSession,
  clearInlineCompose,
  pushNotification,
  dismissNotification,
  clearAllNotifications,
  addMailNotification,
  markAllNotificationsRead,
  dismissMailNotification,
  openContextMenu,
  closeContextMenu,
  setDraggingThread,
  setDragOverFolder,
  clearDragDrop,
  setDraftEdit,
  clearDraftEdit,
  setSearchQuery,
  focusSearch,
  commitSearchQuery,
  setSearchScope,
  setSearchFilter,
  clearSearchFilter,
  clearAllSearchFilters,
  setSearchResults,
  appendSearchResults,
  setSearchSuggestions,
  setSearchPhase,
  setSearchError,
  addRecentQuery,
  removeRecentQuery,
  clearSearch,
  setGraphStatus,
} = mailUiSlice.actions;

// ---------------------------------------------------------------------------
// Selectors
// ---------------------------------------------------------------------------

export const selectSelectedFolderId = (state) => state.mailUi.selectedFolderId;
export const selectSelectedThreadId = (state) => state.mailUi.selectedThreadId;
export const selectExpandedMessageIds = (state) => state.mailUi.expandedMessageIds;
export const selectIsMessageExpanded = (state, messageId) =>
  state.mailUi.expandedMessageIds.includes(messageId);
export const selectUiMode = (state) => state.mailUi.uiMode;
export const selectRibbonState = (state) => state.mailUi.ribbon;
export const selectLayout = (state) => state.mailUi.layout;
export const selectReadingPane = (state) => state.mailUi.readingPane;
export const selectNotifications = (state) => state.mailUi.notifications;
export const selectNotificationHistory = (state) => state.mailUi.notificationHistory;
export const selectUnreadNotificationCount = (state) => state.mailUi.unreadNotificationCount;
export const selectContextMenu = (state) => state.mailUi.contextMenu;
export const selectDragDrop = (state) => state.mailUi.dragDrop;
export const selectSearch = (state) => state.mailUi.search;
export const selectGraphStatus = (state) => state.mailUi.graphStatus;
export const selectScrollPosition = (state, conversationId) =>
  state.mailUi.readingPane.scrollPositions[conversationId] || 0;
export const selectInlineComposeSessionId = (state) =>
  state.mailUi.readingPane.inlineComposeSessionId;
export const selectDraftEditMessageId = (state) => state.mailUi.draftEditMessageId;

export default mailUiSlice.reducer;
